"use client";

import React, { useState, useEffect, Fragment } from "react";
import { useRouter, useParams, useSearchParams } from "next/navigation";
import Image from "next/image";
import { Dialog, Transition } from "@headlessui/react";
import { FitnessClass, INSTRUCTOR_TYPES } from "@/types/fitness-class";
import { toast } from "react-hot-toast";
import { AuthStorage } from "@/lib/storage";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import InlineLoader from "@/components/common/InlineLoader";
import PaymentFirstBooking from "@/components/classes/PaymentFirstBooking";
import {
  getClassImage,
  getClassPrice,
  getClassDateTime,
  getBaseAmount,
  isClassExpired,
} from "@/utils/class-helpers";
import { useAuthSession } from "@/hooks/useAuthSession";
import { Review } from "@/hooks/useClassDetails";
import {
  ClockIcon as FaRegClock,
  MapPinIcon as FaMapMarkerAlt,
  CurrencyRupeeIcon as FaRupee,
  VideoCameraIcon as FaVideo,
  StarIcon as FaStar,
  UserGroupIcon as FaUsers,
  UserIcon as FaUser,
  CalendarIcon as FaCalendarAlt,
  TrophyIcon as FaTrophy,
  HeartIcon as FaHeart,
  SparklesIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  CurrencyRupeeIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { Footer } from "@/components/Footer";

// Helper function to safely format dates
const safeFormatDate = (dateValue: string | undefined | null, options?: { time?: boolean }) => {
  if (!dateValue) return null;

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return null;

    if (options?.time) {
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return date.toLocaleDateString();
  } catch (error) {
    console.error("Error formatting date:", error);
    return null;
  }
};

const HeroSection = ({ fitnessClass }: { fitnessClass: FitnessClass }) => {
  return (
    <div className="relative h-[350px] sm:h-[400px] w-full overflow-hidden zoom-container">
      <div className="absolute inset-0">
        <Image
          src={getClassImage(fitnessClass)}
          alt={fitnessClass.title}
          fill
          className="object-cover"
          priority
        />
      </div>
      <div className="absolute inset-0 bg-black/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-full flex flex-col justify-end pb-6 sm:pb-8">
          <div className="text-white">
            {/* Simple badge */}
            <div className="inline-flex items-center gap-2 bg-white/20 rounded-lg px-2 sm:px-3 py-1 mb-2 sm:mb-3">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span className="text-white text-xs font-medium">
                Premium Experience
              </span>
            </div>

            {/* Category badges */}
            <div className="flex items-center gap-2 mb-3 sm:mb-4 flex-wrap">
              <span className="px-2 sm:px-3 py-1 bg-orange-500 rounded-lg text-xs font-semibold text-white">
                {fitnessClass.category}
              </span>
              <span className="px-2 sm:px-3 py-1 bg-gray-700 rounded-lg text-xs font-semibold text-white">
                {Array.isArray(fitnessClass.difficulty)
                  ? fitnessClass.difficulty[0]
                  : fitnessClass.difficulty}
              </span>
              <span className="px-2 sm:px-3 py-1 bg-green-600 rounded-lg text-xs font-semibold text-white">
                {Array.isArray(fitnessClass.languages) &&
                  fitnessClass.languages.length > 0 &&
                  fitnessClass.languages[0]}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 text-white">
              {fitnessClass.title}
            </h1>

            {/* Stats */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs bg-white/20 p-2 sm:p-3 rounded-lg max-w-4xl">
              {fitnessClass.rating > 0 && <div className="flex items-center gap-2">
                <div className="w-4 h-4 sm:w-5 sm:h-5 bg-orange-500 rounded-md flex items-center justify-center">
                  <FaStar className="text-white w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </div>
                <div>
                  <span className="font-semibold text-white text-xs sm:text-sm">
                    {fitnessClass.rating || 0}
                  </span>
                  <span className="text-white/80 ml-1 font-medium text-xs">
                    ({fitnessClass.reviewCount || 0} reviews)
                  </span>
                </div>
              </div>}

              <div className="flex items-center gap-2">
                <div className="w-4 h-4 sm:w-5 sm:h-5 bg-blue-500 rounded-md flex items-center justify-center">
                  <FaUsers className="text-white w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </div>
                <div>
                  <span className="font-semibold text-white text-xs sm:text-sm">
                    {fitnessClass.spotsLeft}
                  </span>
                  <span className="text-white/80 ml-1 font-medium text-xs">
                    spots left
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="w-4 h-4 sm:w-5 sm:h-5 bg-purple-500 rounded-md flex items-center justify-center">
                  <FaRegClock className="text-white w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </div>
                <div>
                  <span className="font-semibold text-white text-xs sm:text-sm">
                    {fitnessClass.schedule.durationMin} min
                  </span>
                  <span className="text-white/80 ml-1 font-medium text-xs">
                    duration
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="w-4 h-4 sm:w-5 sm:h-5 bg-green-500 rounded-md flex items-center justify-center">
                  <FaRupee className="text-white w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </div>
                <div>
                  <span className="font-semibold text-white text-xs sm:text-sm">
                    ₹{getClassPrice(fitnessClass)}
                  </span>
                  <span className="text-white/80 ml-1 font-medium">
                    {fitnessClass.pricing.mode === "recurringClass"
                      ? "full package"
                      : fitnessClass.pricing.mode === "course"
                        ? "course"
                        : "session"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClassInfoSection = ({ fitnessClass }: { fitnessClass: FitnessClass }) => {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-orange-500 rounded-lg shadow-md flex items-center justify-center">
            <FaTrophy className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              About This Class
            </h2>
            <p className="text-gray-600 text-xs font-medium">
              Discover what makes this experience special
            </p>
          </div>
        </div>

        <p className="text-gray-700 mb-6 leading-relaxed text-sm">
          {fitnessClass.description}
        </p>
      </div>
    </div>
  );
};

const PrerequisitesSection = ({
  fitnessClass,
}: {
  fitnessClass: FitnessClass;
}) => {
  if (!fitnessClass.prerequisites || fitnessClass.prerequisites.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-orange-500 rounded-lg shadow-md flex items-center justify-center">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-gray-900">
              Prerequisites
            </h2>
            <p className="text-gray-600 text-xs sm:text-sm font-medium">
              What you need before joining this class
            </p>
          </div>
        </div>

        <div className="space-y-2 sm:space-y-3">
          {fitnessClass.prerequisites.map(
            (prerequisite: string, idx: number) => (
              <div
                key={idx}
                className="flex items-start gap-2 sm:gap-3 bg-orange-50 p-3 sm:p-4 rounded-lg border border-orange-200"
              >
                <div className="flex-shrink-0">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-medium text-orange-800">
                    {prerequisite}
                  </p>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

const SidebarSection = ({
  fitnessClass,
  handleAction,
  isAuthLoading,
  isBookingLoading,
  isFavorite,
  handleToggleFavorite,
  classId: _classId,
  isClassBooked,
  router,
  user,
}: {
  fitnessClass: FitnessClass;
  handleAction: (action: string) => void;
  isAuthLoading: boolean;
  isBookingLoading: boolean;
  isFavorite: boolean;
  handleToggleFavorite: () => void;
  classId: string;
  isClassBooked: boolean;
  router: any;
  user: any;
}) => {
  console.log("Rendering SidebarSection", _classId);
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden sticky top-20">
      <div className="p-4 sm:p-5">
        {/* Instructor Section */}
        <div className="flex items-center gap-3 sm:gap-3 mb-5 sm:mb-6 p-3 sm:p-3 rounded-lg bg-gray-50">
          <div className="relative w-14 h-14 sm:w-12 sm:h-12">
            {fitnessClass.instructorImage ? (
              <Image
                src={fitnessClass.instructorImage}
                alt={fitnessClass.instructorName || "Instructor"}
                fill
                className="rounded-lg object-cover"
              />
            ) : (
              <div className="w-14 h-14 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                <FaUser className="text-gray-400 w-6 h-6 sm:w-5 sm:h-5" />
              </div>
            )}

            <div className="absolute -top-1 -right-1 w-4 h-4 sm:w-4 sm:h-4 bg-green-500 rounded-full border border-white flex items-center justify-center">
              <FaStar className="text-white w-2 h-2 sm:w-2 sm:h-2" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base sm:text-sm font-bold text-gray-900 truncate mb-1">
              {fitnessClass.instructorName || "Unknown Instructor"}
            </p>
            <div className="flex items-center gap-1 text-sm sm:text-xs text-gray-600">
              <FaStar className="text-orange-500 w-3.5 h-3.5 sm:w-2.5 sm:h-2.5" />
              <span className="font-medium">{fitnessClass.rating || 0}</span>
              <span>•</span>
              <span className="font-medium">Verified</span>
            </div>
          </div>
        </div>

        {/* Class Details */}
        <div className="space-y-3 sm:space-y-3 mb-5 sm:mb-6">
          {[
            {
              icon: <FaRegClock className="text-white" />,
              label: "Duration",
              value: `${fitnessClass.schedule.durationMin} min`,
              bgColor: "bg-blue-50",
              iconBg: "bg-blue-500",
              borderColor: "border-blue-200",
            },
            {
              icon: <FaMapMarkerAlt className="text-white" />,
              label: "Format",
              value: fitnessClass.format || "Online",
              bgColor: "bg-green-50",
              iconBg: "bg-green-500",
              borderColor: "border-green-200",
            },
            {
              icon: <FaRupee className="text-white" />,
              label: "Price",
              value: (() => {
                if (fitnessClass.pricing.mode === "recurringClass") {
                  return (
                    <div>
                      <div className="text-sm sm:text-sm font-semibold text-gray-900">
                        ₹{fitnessClass.pricing.packagePrice}
                      </div>
                      <div className="text-xs text-gray-600">
                        Full Package ({fitnessClass.pricing.totalSessions}{" "}
                        sessions)
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Or ₹{fitnessClass.pricing.sessionPrice}/session
                      </div>
                    </div>
                  );
                } else {
                  return `₹${getClassPrice(fitnessClass)}`;
                }
              })(),
              bgColor: "bg-orange-50",
              iconBg: "bg-orange-500",
              borderColor: "border-orange-200",
            },
          ].map(({ icon, label, value, bgColor, iconBg, borderColor }, idx) => (
            <div
              key={idx}
              className={`${bgColor} p-3 sm:p-3 rounded-lg border ${borderColor} flex items-center gap-3 sm:gap-3`}
            >
              <div
                className={`w-7 h-7 sm:w-6 sm:h-6 ${iconBg} rounded-md flex items-center justify-center flex-shrink-0`}
              >
                {React.cloneElement(icon, {
                  className: "w-4 h-4 sm:w-3 sm:h-3 text-white",
                })}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm sm:text-sm text-gray-600 font-medium">
                  {label}
                </div>
                <div className="text-sm sm:text-sm font-semibold text-gray-900">
                  {typeof value === "string" ? (
                    <div className="truncate">{value}</div>
                  ) : (
                    value
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 sm:space-y-3">
          {/* JOIN LIVE CLASS BUTTON - Only for enrolled students, 10 min before session */}
          {isClassBooked && (() => {
            const now = new Date();
            const schedule = fitnessClass.schedule;
            let canJoin = false;
            let nextSessionTime: Date | null = null;
            let minutesUntil = Infinity;

            if (schedule.kind === 'single' && schedule.date) {
              const sessionStart = new Date(schedule.date);
              const sessionEnd = new Date(sessionStart.getTime() + (schedule.durationMin || 60) * 60000);
              const minUntilStart = (sessionStart.getTime() - now.getTime()) / 60000;

              if (now <= sessionEnd && minUntilStart <= 10) {
                // Within 10-min window or class already started (but not yet ended)
                canJoin = true;
                nextSessionTime = sessionStart;
                minutesUntil = minUntilStart;
              } else if (minUntilStart > 10) {
                nextSessionTime = sessionStart;
                minutesUntil = minUntilStart;
              }
            } else if (schedule.kind === 'recurring' && schedule.startDate && schedule.startTime) {
              // Find the NEXT scheduled session date from today
              const [h, m] = schedule.startTime.split(':').map(Number);
              const searchDate = new Date();
              // Look ahead up to 14 days to find the next session
              for (let i = 0; i < 14; i++) {
                const candidate = new Date(searchDate);
                candidate.setDate(searchDate.getDate() + i);
                candidate.setHours(h, m, 0, 0);

                const dayOfWeek = candidate.getDay();
                const isValidDay = schedule.weekdayMask?.includes(dayOfWeek);
                if (!isValidDay) continue;

                // Make sure it's within the recurring range
                const rangeStart = new Date(schedule.startDate);
                const rangeEnd = schedule.endDate ? new Date(schedule.endDate) : null;
                if (candidate < rangeStart) continue;
                if (rangeEnd && candidate > rangeEnd) continue;

                const sessionEnd = new Date(candidate.getTime() + (schedule.durationMin || 60) * 60000);
                if (now > sessionEnd) continue; // already ended, skip

                const minUntil = (candidate.getTime() - now.getTime()) / 60000;
                nextSessionTime = candidate;
                minutesUntil = minUntil;
                if (minUntil <= 10) canJoin = true;
                break;
              }
            }

            if (!nextSessionTime) return null; // no upcoming session found — hide button entirely

            const handleJoin = () => {
              if (!canJoin) {
                const minsLeft = Math.ceil(minutesUntil - 10);
                if (minsLeft > 60) {
                  const h = Math.floor(minsLeft / 60);
                  const m = minsLeft % 60;
                  toast.error(`Join button opens ${h}h ${m}m before the class starts.`);
                } else {
                  toast.error(`Join button opens 10 min before the class starts.`);
                }
                return;
              }
              router.push(`/classes/${_classId}/live?username=${encodeURIComponent(user?.name || "Student")}`);
            };

            return (
              <button
                onClick={(e) => { e.stopPropagation(); handleJoin(); }}
                className={`w-full px-4 sm:px-4 h-[42px] sm:h-[48px] rounded-lg font-bold transition-all duration-300 shadow-lg text-base sm:text-sm flex items-center justify-center gap-2 mb-3 ${canJoin
                  ? 'bg-red-600 text-white hover:bg-red-700 hover:shadow-xl animate-pulse'
                  : 'bg-gray-100 text-gray-500 cursor-not-allowed border border-gray-200'
                  }`}
              >
                {canJoin ? (
                  <>
                    <div className="w-2.5 h-2.5 bg-white rounded-full animate-ping" />
                    <span>JOIN LIVE CLASS</span>
                  </>
                ) : (
                  <>
                    <FaRegClock className="w-4 h-4" />
                    <span>
                      {Math.ceil(minutesUntil) > 60
                        ? `Starts in ${Math.floor(minutesUntil / 60)}h ${Math.ceil(minutesUntil % 60)}m`
                        : `Starts in ${Math.ceil(minutesUntil)} min`
                      }
                    </span>
                  </>
                )}
              </button>
            );
          })()}

          <button
            onClick={() =>
              isClassBooked || isClassExpired(fitnessClass)
                ? null
                : handleAction("booking")
            }
            disabled={
              isAuthLoading ||
              isBookingLoading ||
              isClassBooked ||
              isClassExpired(fitnessClass)
            }
            className={`w-full px-4 sm:px-4 h-[42px] sm:h-[48px] rounded-lg font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl text-base sm:text-sm ${isClassBooked
              ? "bg-green-500 text-white"
              : isClassExpired(fitnessClass)
                ? "bg-gray-400 text-white"
                : "bg-orange-500 text-white hover:bg-orange-600"
              }`}
          >
            {isAuthLoading ? (
              <InlineLoader color="white" text="Loading..." />
            ) : isBookingLoading ? (
              <InlineLoader color="white" text="Processing..." />
            ) : isClassBooked ? (
              <div className="flex items-center justify-center gap-2 sm:gap-2">
                <CheckCircleIcon className="w-5 h-5 sm:w-4 sm:h-4" />
                <span>Enrolled</span>
              </div>
            ) : isClassExpired(fitnessClass) ? (
              <div className="flex items-center justify-center gap-2 sm:gap-2">
                <FaCalendarAlt className="w-4 h-4 sm:w-4 sm:h-4" />
                <span>Class Ended</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 sm:gap-2">
                <FaCalendarAlt className="w-4 h-4 sm:w-4 sm:h-4" />
                <span>Enroll Now</span>
              </div>
            )}
          </button>

          <button
            onClick={handleToggleFavorite}
            className={`w-full px-4 sm:px-4 h-[42px] sm:h-[48px] rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg text-base sm:text-sm flex items-center justify-center gap-2 sm:gap-2 ${isFavorite
              ? "bg-pink-500 text-white"
              : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
              }`}
          >
            <FaHeart className="w-4 h-4 sm:w-3 sm:h-3" />
            <span>
              {isFavorite ? "Remove from Wishlist" : "Add to Wishlist"}
            </span>
          </button>
        </div>

        {/* Class Info */}
        <div className="mt-5 sm:mt-6 pt-4 sm:pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-3 sm:gap-3 text-center">
            <div className="bg-gray-50 p-3 sm:p-3 rounded-lg">
              <div className="text-xl sm:text-lg font-bold text-orange-500">
                {fitnessClass.spotsLeft || 0}
              </div>
              <div className="text-sm sm:text-sm text-gray-600 font-medium">
                Spots Left
              </div>
            </div>
            <div className="bg-gray-50 p-3 sm:p-3 rounded-lg">
              <div className="text-xl sm:text-lg font-bold text-gray-900">
                {fitnessClass.capacity || 0}
              </div>
              <div className="text-sm sm:text-sm text-gray-600 font-medium">
                Max Capacity
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClassDetailClient = () => {
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();
  const classId = params?.id as string;
  const paymentSuccess = searchParams?.get('payment_success');

  const [fitnessClass, setFitnessClass] = useState<FitnessClass | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [actionType, setActionType] = useState("");
  const [isFavorite, setIsFavorite] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, isLoading: isAuthLoading } = useAuthSession();

  // Review state variables
  const [reviews, setReviews] = useState<Review[]>([]);
  const [newReview, setNewReview] = useState("");
  const [newRating, setNewRating] = useState(5);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [fetchingReviews, setFetchingReviews] = useState(true);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "rating">(
    "newest"
  );
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [userHasReviewed, setUserHasReviewed] = useState(false);

  // Booking state
  const [isBookingLoading, setIsBookingLoading] = useState(false);
  const [isClassBooked, setIsClassBooked] = useState(false);
  // Tick every minute so the Join button appears/disappears automatically
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  // Force fresh data on mount - prevent stale cached data
  useEffect(() => {
    // Clear any cached data for this route
    if (typeof window !== 'undefined' && 'caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.open(name).then((cache) => {
            cache.delete(window.location.href);
            cache.delete(new URL(window.location.href).pathname);
          });
        });
      });
    }
  }, []);

  // 🔴 DEBUG - THIS WILL SHOW IN BROWSER CONSOLE
  console.log("🔴🔴🔴 CLASS PAGE LOADED 🔴🔴🔴", {
    user: user?.id,
    classId,
    isAuthLoading,
    paymentSuccess,
    isClassBooked
  });



  // Fetch class data
  useEffect(() => {
    const fetchClassData = async () => {
      if (!classId) return;

      try {
        setIsLoading(true);
        setError(null);

        console.log("Fetching class data for ID:", classId);
        // Add cache-busting timestamp to ensure fresh data
        const timestamp = new Date().getTime();
        const response = await fetch(`/api/classes/${classId}?_t=${timestamp}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Class not found");
          }
          throw new Error(`Failed to fetch class data: ${response.status}`);
        }

        const data = await response.json();
        console.log("Class data received:", data);

        // Extract class data from API response
        const classData = data.class || data;
        console.log("Class data extracted:", classData);

        // Simple conversion to FitnessClass format
        const fitnessClass: FitnessClass = {
          ...classData,
          id: classData.id || classData._id,
          // Ensure we have the minimum required fields
          title: classData.title || "",
          description: classData.description || "",
          instructorId: classData.instructorId || "",
          instructorName:
            classData.instructorName ||
            classData.instructor ||
            "Unknown Instructor",
          instructorType: classData.instructorType || "yogaInstructor",
          category: classData.category || "General",
          classType: classData.classType || "live",
          difficulty: Array.isArray(classData.difficulty)
            ? classData.difficulty
            : [classData.difficulty || "all"],
          ageBand: classData.ageBand || "all",
          // Schedule with validation - wrapped in try-catch
          schedule: (() => {
            try {
              const sched = classData.schedule;
              console.log("🔍 Processing schedule:", JSON.stringify(sched));

              if (!sched) {
                console.log("⚠️ No schedule found, using default");
                return {
                  kind: "single" as const,
                  date: new Date().toISOString(),
                  durationMin: 60,
                  timeZone: "Asia/Kolkata",
                };
              }

              // Validate and clean schedule dates
              const cleanedSchedule = { ...sched };

              // For single schedule, validate date
              if (cleanedSchedule.kind === "single") {
                console.log("📅 Processing single schedule, date:", cleanedSchedule.date);
                if (cleanedSchedule.date) {
                  try {
                    const d = new Date(cleanedSchedule.date);
                    console.log("📅 Date object created:", d.toString(), "isNaN:", isNaN(d.getTime()));
                    if (isNaN(d.getTime())) {
                      console.warn("❌ Invalid single schedule date (NaN):", cleanedSchedule.date);
                      cleanedSchedule.date = new Date().toISOString();
                    } else {
                      // Check if date is within valid range for toISOString (year 0-275760)
                      const year = d.getFullYear();
                      if (year < 0 || year > 275760) {
                        console.warn("❌ Date year out of range:", year);
                        cleanedSchedule.date = new Date().toISOString();
                      } else {
                        try {
                          const isoString = d.toISOString();
                          console.log("✅ Converted to ISO:", isoString);
                          cleanedSchedule.date = isoString;
                        } catch (isoError) {
                          console.error("❌ Error calling toISOString:", isoError, "Date:", d.toString());
                          cleanedSchedule.date = new Date().toISOString();
                        }
                      }
                    }
                  } catch (e) {
                    console.error("❌ Error parsing single schedule date:", e);
                    cleanedSchedule.date = new Date().toISOString();
                  }
                } else {
                  console.log("⚠️ No date in single schedule, using current");
                  cleanedSchedule.date = new Date().toISOString();
                }
              }

              // For recurring schedule, validate dates
              if (cleanedSchedule.kind === "recurring") {
                console.log("🔄 Processing recurring schedule");
                if (cleanedSchedule.startDate) {
                  try {
                    const d = new Date(cleanedSchedule.startDate);
                    if (isNaN(d.getTime())) {
                      console.warn("❌ Invalid recurring startDate, removing");
                      delete cleanedSchedule.startDate;
                    } else {
                      const year = d.getFullYear();
                      if (year < 0 || year > 275760) {
                        console.warn("❌ startDate year out of range:", year);
                        delete cleanedSchedule.startDate;
                      } else {
                        try {
                          cleanedSchedule.startDate = d.toISOString();
                        } catch (isoError) {
                          console.error("❌ Error converting startDate to ISO:", isoError);
                          delete cleanedSchedule.startDate;
                        }
                      }
                    }
                  } catch (e) {
                    console.error("❌ Error parsing recurring startDate:", e);
                    delete cleanedSchedule.startDate;
                  }
                }
                if (cleanedSchedule.endDate) {
                  try {
                    const d = new Date(cleanedSchedule.endDate);
                    if (isNaN(d.getTime())) {
                      console.warn("❌ Invalid recurring endDate, removing");
                      delete cleanedSchedule.endDate;
                    } else {
                      const year = d.getFullYear();
                      if (year < 0 || year > 275760) {
                        console.warn("❌ endDate year out of range:", year);
                        delete cleanedSchedule.endDate;
                      } else {
                        try {
                          cleanedSchedule.endDate = d.toISOString();
                        } catch (isoError) {
                          console.error("❌ Error converting endDate to ISO:", isoError);
                          delete cleanedSchedule.endDate;
                        }
                      }
                    }
                  } catch (e) {
                    console.error("❌ Error parsing recurring endDate:", e);
                    delete cleanedSchedule.endDate;
                  }
                }
              }

              // For course schedule, validate session dates
              if (cleanedSchedule.kind === "course" && Array.isArray(cleanedSchedule.sessionDates)) {
                console.log("📚 Processing course schedule with", cleanedSchedule.sessionDates.length, "sessions");
                cleanedSchedule.sessionDates = cleanedSchedule.sessionDates.filter((dateStr: string) => {
                  try {
                    const d = new Date(dateStr);
                    if (isNaN(d.getTime())) {
                      console.warn("❌ Invalid course session date:", dateStr);
                      return false;
                    }
                    const year = d.getFullYear();
                    if (year < 0 || year > 275760) {
                      console.warn("❌ Session date year out of range:", year);
                      return false;
                    }
                    try {
                      d.toISOString();
                      return true;
                    } catch (isoError) {
                      console.warn("❌ Session date cannot be converted to ISO:", dateStr);
                      return false;
                    }
                  } catch (e) {
                    console.warn("❌ Error parsing course session date:", dateStr, e);
                    return false;
                  }
                }).map((dateStr: string) => {
                  try {
                    return new Date(dateStr).toISOString();
                  } catch (e) {
                    return dateStr;
                  }
                });
              }

              console.log("✅ Schedule processed successfully:", cleanedSchedule.kind);
              return cleanedSchedule;
            } catch (error) {
              console.error("💥 FATAL ERROR processing schedule:", error);
              // Return safe default schedule
              return {
                kind: "single" as const,
                date: new Date().toISOString(),
                durationMin: 60,
                timeZone: "Asia/Kolkata",
              };
            }
          })(),
          pricing: classData.pricing || {
            mode: "payPerSession",
            sessionPrice: classData.price || 0,
          },
          format: "online",
          languages: Array.isArray(classData.languages)
            ? classData.languages
            : [classData.languages || "English"],
          capacity:
            typeof classData.capacity === "number" ? classData.capacity : 10,
          spotsLeft:
            typeof classData.spotsLeft === "number" ? classData.spotsLeft : 10,
          waitlistEnabled: classData.waitlistEnabled || false,
          rating: classData.rating || 0,
          reviewCount: classData.reviewCount || 0,
          status: classData.status || "active",
          isPublished: classData.isPublished !== false,
          createdAt: new Date(classData.createdAt || Date.now()),
          updatedAt: new Date(classData.updatedAt || Date.now()),
          // Optional fields
          media:
            classData.media || classData.imageUrl
              ? {
                image: classData.imageUrl || classData.media?.image,
                videoPreview:
                  classData.videoPreviewUrl || classData.media?.videoPreview,
              }
              : undefined,
          requirements: Array.isArray(classData.requirements)
            ? classData.requirements
            : [],
          benefits: Array.isArray(classData.benefits) ? classData.benefits : [],
          features: Array.isArray(classData.features) ? classData.features : [],
          equipmentProvided: Array.isArray(classData.equipmentProvided)
            ? classData.equipmentProvided
            : [],
          equipmentRequired: Array.isArray(classData.equipmentRequired)
            ? classData.equipmentRequired
            : [],
          tags: Array.isArray(classData.tags) ? classData.tags : [],
          prerequisites: Array.isArray(classData.prerequisites)
            ? classData.prerequisites
            : [],
          whatToExpect: Array.isArray(classData.whatToExpect)
            ? classData.whatToExpect
            : [],
          whatToAvoid: Array.isArray(classData.whatToAvoid)
            ? classData.whatToAvoid
            : [],
          achievements: Array.isArray(classData.achievements)
            ? classData.achievements
            : [],
        };

        // Add compatibility properties for legacy component usage
        setFitnessClass(fitnessClass); // Set the fitness class data
      } catch (error) {
        console.error("Error fetching class:", error);
        setError(
          error instanceof Error ? error.message : "Failed to load class"
        );
        toast.error(
          error instanceof Error ? error.message : "Failed to load class"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchClassData();
  }, [classId]); // Include all dependencies

  // Review Helper Functions
  const getReviewerName = (review: Review) => {
    return review.userName || review.reviewerName || "Anonymous";
  };

  const getReviewerId = (review: Review) => {
    return review.userId || review.reviewer || "";
  };

  // Fetch reviews from API
  const fetchReviews = React.useCallback(async () => {
    setFetchingReviews(true);
    setReviewError(null);
    try {
      console.log("Fetching reviews for class:", classId);
      // Add cache-busting timestamp
      const timestamp = new Date().getTime();
      const res = await fetch(`/api/classes/${classId}/reviews?_t=${timestamp}`, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          "Pragma": "no-cache",
          "Expires": "0"
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch reviews: ${res.status}`);
      }

      const data = await res.json();
      console.log("Reviews fetched successfully:", data);
      console.log("Number of reviews:", data.reviews?.length || 0);
      setReviews(data.reviews || []);
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setReviewError("Failed to load reviews");
      toast.error("Failed to load reviews");
    } finally {
      setFetchingReviews(false);
    }
  }, [classId]);

  // Fetch reviews when classId changes
  React.useEffect(() => {
    if (classId) {
      fetchReviews();
    }
  }, [classId, fetchReviews]);

  // Check if current user has already reviewed this class
  React.useEffect(() => {
    if (user && reviews.length > 0) {
      const hasUserReviewed = reviews.some(
        (review) =>
          getReviewerId(review) === user.id ||
          getReviewerName(review) === user.name
      );
      setUserHasReviewed(hasUserReviewed);
    } else {
      setUserHasReviewed(false);
    }
  }, [user, reviews]);

  // Check if current user has already booked this class
  const checkBookingStatus = React.useCallback(async () => {
    if (!user || !classId || isAuthLoading) return;

    try {
      console.log("🚨 [LOUD LOG] Checking booking status...");
      console.log("🚨 [LOUD LOG] Target Class ID:", classId);
      console.log("🚨 [LOUD LOG] User ID:", user.id);

      const fetchUrl = `/api/bookings/my-bookings/${user.id}`;
      console.log("🚨 [LOUD LOG] Fetching URL:", fetchUrl);

      const response = await fetch(fetchUrl, {
        credentials: "include",
        cache: "no-store",
      });

      console.log("🚨 [LOUD LOG] Response Status:", response.status);

      if (response.ok) {
        const { bookings, _debug } = await response.json();
        console.log("🚨 [LOUD LOG] _debug from server:", _debug);
        console.log("🚨 [LOUD LOG] Bookings Array:", bookings);
        console.log("🚨 [LOUD LOG] Bookings Length:", bookings?.length || 0);

        const isClassBooked = bookings?.some(
          (booking: {
            classId: string;
            status: string;
            paymentStatus: string;
          }) => {
            // Convert both to strings and compare
            const bookingClassId = booking.classId?.toString();
            const currentClassId = classId?.toString();

            const classIdMatches = bookingClassId === currentClassId;
            const statusMatches = booking.status === "confirmed" || booking.status === "completed";
            const paymentMatches = booking.paymentStatus === "paid";
            const matches = classIdMatches && statusMatches && paymentMatches;

            console.log("🔍 Checking booking:", {
              bookingClassId,
              currentClassId,
              classIdMatches,
              status: booking.status,
              statusMatches,
              paymentStatus: booking.paymentStatus,
              paymentMatches,
              FINAL_MATCH: matches
            });

            return matches;
          }
        );
        console.log("✅✅✅ FINAL RESULT - Is class booked?", isClassBooked);
        setIsClassBooked(isClassBooked);
      }
    } catch (error) {
      console.error("Error checking booking status:", error);
    }
  }, [user, classId, isAuthLoading]);

  // Initial booking status check on mount and when dependencies change
  React.useEffect(() => {
    if (user && classId && !isAuthLoading) {
      console.log("🔄 Initial booking status check on mount");
      checkBookingStatus();
    }
  }, [user, classId, isAuthLoading, checkBookingStatus]);

  // Poll for booking success when redirecting from payment
  React.useEffect(() => {
    // Only start polling if we have a success flag and the class isn't booked yet
    if (paymentSuccess === 'true' && !isClassBooked) {
      console.log("🎉 Payment success URL detected. Starting robust polling for booking confirmation...");

      let attempts = 0;
      const maxAttempts = 15; // 15 attempts * 1000ms = 15 seconds

      // Initial check immediately
      if (user && !isAuthLoading) checkBookingStatus();

      const pollInterval = setInterval(() => {
        attempts++;
        console.log(`♻️ Polling booking status... Attempt ${attempts}/${maxAttempts}`);

        // Only check if we have a user and auth is done
        if (user && !isAuthLoading) {
          checkBookingStatus();
        } else {
          console.log("   ⏳ Waiting for user authentication to load...");
        }

        if (attempts >= maxAttempts) {
          console.log("⚠️ Polling timeout reached. Stopping checks.");
          clearInterval(pollInterval);
        }
      }, 1000);

      // Cleanup when component unmounts or dependencies change (e.g. isClassBooked becomes true)
      return () => {
        console.log("🛑 Stopping booking poller");
        clearInterval(pollInterval);
      };
    }

    if (paymentSuccess === 'true' && isClassBooked) {
      console.log("✅ Class is definitively confirmed as booked!");
    }
  }, [paymentSuccess, isClassBooked, user, isAuthLoading, checkBookingStatus]);

  // Re-check booking status when page regains focus (user returns from payment)
  React.useEffect(() => {
    const handleFocus = () => {
      console.log("Page focused - rechecking booking status");
      checkBookingStatus();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [checkBookingStatus]);

  // Check if class is in user's favorites - same logic as ClassList
  useEffect(() => {
    const loadFavoriteStatus = async () => {
      if (!user || !classId || isAuthLoading) return;

      try {
        const response = await fetch("/api/wishlist", {
          credentials: "include",
        });

        if (response.ok) {
          const { wishlist } = await response.json();
          const isClassInWishlist = wishlist.some(
            (item: { classId: string }) => item.classId === classId
          );
          setIsFavorite(isClassInWishlist);
        }
      } catch (error) {
        console.error("Error checking favorite status:", error);
      }
    };

    loadFavoriteStatus();
  }, [user, classId, isAuthLoading]);

  // Calculate review statistics

  const handleBookClass = async () => {
    if (!user || !fitnessClass) return;

    setIsBookingLoading(true);
    try {
      console.log("Booking class:", classId, "User:", user);

      // Get token from AuthStorage (cookies) instead of localStorage
      const token = AuthStorage.getToken();
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };

      // Add Authorization header if token exists
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      console.log(
        "Making booking request with token:",
        token ? "Present" : "Missing"
      );

      const response = await fetch(`/api/classes/${classId}/book`, {
        method: "POST",
        headers,
        credentials: "include", // Include cookies for authentication
        body: JSON.stringify({
          notes: "",
          paymentMethod: "razorpay",
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to book class");
      }

      const data = await response.json();
      console.log("Booking response:", data);

      // Old booking flow - this is now handled by PaymentFirstBooking component
      toast.success("Booking created! Please use the new payment flow.");
      handleCloseModal();
    } catch (error) {
      console.error("Error booking class:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to book class"
      );
    } finally {
      setIsBookingLoading(false);
    }
  };

  const renderStars = (rating: number, size: "sm" | "md" | "lg" = "md") => {
    const sizeClasses = {
      sm: "w-4 h-4",
      md: "w-5 h-5",
      lg: "w-6 h-6",
    };

    return [...Array(5)].map((_, index) => {
      const starValue = index + 1;
      return (
        <StarIconSolid
          key={index}
          className={`${sizeClasses[size]} transition-all duration-200 ${rating >= starValue
            ? "text-[#ff7043] drop-shadow-lg"
            : "text-[#463a33]/20"
            }`}
        />
      );
    });
  };

  const handleAddReview = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if auth is still loading
    if (isAuthLoading) {
      toast.error("Please wait while we verify your login status...");
      return;
    }

    if (!user) {
      toast.error("Please login to add a review");
      return;
    }

    if (!isClassBooked) {
      toast.error("You must enroll in this class before sharing your experience.");
      return;
    }

    if (!newReview.trim()) {
      toast.error("Please write a review");
      return;
    }

    setReviewLoading(true);
    setReviewError(null);

    try {
      // Get token from AuthStorage (cookies) instead of localStorage
      const token = AuthStorage.getToken();
      console.log("Auth token for review:", token ? "Present" : "Missing");

      const requestBody = {
        review: newReview,
        rating: newRating,
      };

      console.log("Request body:", requestBody);

      const res = await fetch(`/api/classes/${classId}/reviews`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      const responseText = await res.text();
      console.log("Raw response:", responseText);

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Failed to parse response as JSON:", parseError);
        throw new Error(`Server error: ${res.status} - ${responseText}`);
      }

      console.log("Add review response:", {
        status: res.status,
        ok: res.ok,
        data,
      });

      if (res.ok) {
        console.log("Review submission successful, data:", data);
        // Clear the form immediately
        setNewReview("");
        setNewRating(5);
        setUserHasReviewed(true);
        toast.success("Review added successfully!");

        // Refresh reviews to get the latest data with a small delay
        setTimeout(async () => {
          console.log("Refreshing reviews after submission...");
          await fetchReviews();
        }, 500);
      } else {
        const errorMessage =
          data.error || `HTTP ${res.status}: Failed to add review`;
        console.error("Review submission failed:", errorMessage);
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error("Error adding review:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to add review";
      setReviewError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setReviewLoading(false);
    }
  };

  // Handle action (booking, joining session, etc.)
  const handleAction = (action: string) => {
    if (isAuthLoading) {
      toast.loading("Checking authentication...", { duration: 1000 });
      return;
    }

    if (!user) {
      toast.error("Please log in to continue");
      const callbackUrl =
        action === "livestream"
          ? `/classes/${classId}/live`
          : `/classes/${classId}`;
      router.push(`/login?callbackUrl=${callbackUrl}`);
      return;
    }

    // User is authenticated, proceed with action
    setActionType(action);
    setOpenModal(true);
  };

  // Handle favorites functionality
  const handleToggleFavorite = async () => {
    // Check if auth is still loading
    if (isAuthLoading) {
      toast.error("Please wait while we verify your login status...");
      return;
    }

    if (!user) {
      toast.error("Please login to add to wishlist");
      return;
    }

    try {
      const response = await fetch("/api/wishlist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          classId: fitnessClass?.id,
          action: isFavorite ? "remove" : "add",
        }),
        credentials: "include",
      });

      if (response.ok) {
        setIsFavorite(!isFavorite);
        toast.success(
          isFavorite ? "Removed from favorites" : "Added to favorites"
        );
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || "Failed to update favorites");
      }
    } catch (error) {
      console.error("Error updating favorites:", error);
      toast.error("Failed to update favorites");
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setOpenModal(false);
    setActionType("");
  };

  // Handle confirm action
  const handleConfirmAction = async () => {
    if (actionType === "join") {
      // Navigate to live session page
      console.log("Navigating to live session page");
      router.push(`/classes/${classId}/live`);
      handleCloseModal();
    } else if (actionType === "booking") {
      // Handle booking logic here - use existing handleBookClass function
      await handleBookClass();
    }
  };

  if (isLoading || isAuthLoading) {
    return <LoadingSpinner fullScreen={true} />;
  }

  if (error || !fitnessClass) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50">
        <div className="flex flex-col items-center gap-4 text-center max-w-md mx-auto p-8 zoom-container">
          <div className="text-4xl mb-2">😔</div>
          <h2 className="text-xl font-bold text-gray-800">Class Not Found</h2>
          <p className="text-gray-600 text-sm mb-6">
            {error ||
              "The class you're looking for doesn't exist or has been removed."}
          </p>
          <button
            onClick={() => router.push("/classes")}
            className="px-6 py-2 bg-[#ff7043] text-white rounded-lg hover:bg-[#ff7043]/90 transition-colors text-sm font-medium"
          >
            Browse All Classes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {fitnessClass && (
        <>
          <HeroSection fitnessClass={fitnessClass} />

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 zoom-container">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Left Column - Main Content */}
              <div className="lg:col-span-8 space-y-6">
                <ClassInfoSection fitnessClass={fitnessClass} />

                {/* Schedule Section */}
                {fitnessClass.schedule && (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 bg-blue-500 rounded-lg shadow-md flex items-center justify-center">
                          <FaCalendarAlt className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h2 className="text-base sm:text-lg font-bold text-gray-900">
                            Class Schedule
                          </h2>
                          <p className="text-gray-600 text-xs sm:text-sm font-medium">
                            When this experience takes place
                          </p>
                        </div>
                      </div>

                      {/* Schedule details */}
                      <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg text-center">
                          <div className="text-base sm:text-lg font-bold text-blue-500">
                            {fitnessClass.schedule.durationMin}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-600 font-medium">
                            Minutes
                          </div>
                        </div>
                        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg text-center">
                          <div className="text-base sm:text-lg font-bold text-gray-900">
                            {fitnessClass.schedule.kind}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-600 font-medium">
                            Schedule Type
                          </div>
                        </div>
                      </div>

                      {/* Recurring Class Details */}
                      {fitnessClass.schedule.kind === "recurring" && (
                        <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-200">
                          <h3 className="font-semibold text-blue-900 mb-2 sm:mb-3 text-sm sm:text-base">
                            Recurring Schedule Details
                          </h3>
                          <div className="space-y-2">
                            {fitnessClass.schedule.kind === "recurring" &&
                              fitnessClass.schedule.weekdayMask && (
                                <div className="flex flex-wrap gap-1 sm:gap-2">
                                  <span className="text-xs sm:text-sm text-blue-800 font-medium">
                                    Days:
                                  </span>
                                  {fitnessClass.schedule.weekdayMask.map(
                                    (dayNum: number, idx: number) => {
                                      const dayNames = [
                                        "Sun",
                                        "Mon",
                                        "Tue",
                                        "Wed",
                                        "Thu",
                                        "Fri",
                                        "Sat",
                                      ];
                                      return (
                                        <span
                                          key={idx}
                                          className="px-2 py-1 bg-blue-200 text-blue-800 rounded text-xs font-medium"
                                        >
                                          {dayNames[dayNum]}
                                        </span>
                                      );
                                    }
                                  )}
                                </div>
                              )}
                            {fitnessClass.schedule.kind === "recurring" &&
                              fitnessClass.schedule.startTime && (
                                <div className="text-xs sm:text-sm">
                                  <span className="text-blue-800 font-medium">
                                    Time:
                                  </span>
                                  <span className="text-blue-700 ml-2">
                                    {fitnessClass.schedule.startTime}
                                  </span>
                                </div>
                              )}
                            {fitnessClass.schedule.totalSessions && (
                              <div className="text-xs sm:text-sm">
                                <span className="text-blue-800 font-medium">
                                  Total Sessions:
                                </span>
                                <span className="text-blue-700 ml-2">
                                  {fitnessClass.schedule.totalSessions}
                                </span>
                              </div>
                            )}
                            {fitnessClass.schedule.startDate && (
                              <div className="text-xs sm:text-sm">
                                <span className="text-blue-800 font-medium">
                                  Starts:
                                </span>
                                <span className="text-blue-700 ml-2">
                                  {new Date(
                                    fitnessClass.schedule.startDate
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                            {fitnessClass.schedule.endDate && (
                              <div className="text-xs sm:text-sm">
                                <span className="text-blue-800 font-medium">
                                  Ends:
                                </span>
                                <span className="text-blue-700 ml-2">
                                  {new Date(
                                    fitnessClass.schedule.endDate
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Course Schedule Details */}
                      {fitnessClass.schedule.kind === "course" &&
                        fitnessClass.schedule.sessionDates && (
                          <div className="bg-green-50 p-3 sm:p-4 rounded-lg border border-green-200">
                            <h3 className="font-semibold text-green-900 mb-2 sm:mb-3 text-sm sm:text-base">
                              Course Schedule (
                              {fitnessClass.schedule.sessionDates.length}{" "}
                              Sessions)
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                              {fitnessClass.schedule.sessionDates
                                .slice(0, 6)
                                .map((date: string, idx: number) => (
                                  <div
                                    key={idx}
                                    className="text-xs sm:text-sm bg-white p-2 rounded border border-green-200"
                                  >
                                    <span className="text-green-800 font-medium">
                                      Session {idx + 1}:
                                    </span>
                                    <span className="text-green-700 ml-2">
                                      {new Date(date).toLocaleDateString()} at{" "}
                                      {new Date(date).toLocaleTimeString(
                                        "en-US",
                                        { hour: "2-digit", minute: "2-digit" }
                                      )}
                                    </span>
                                  </div>
                                ))}
                              {fitnessClass.schedule.sessionDates.length >
                                6 && (
                                  <div className="text-xs sm:text-sm text-green-600 font-medium col-span-2 text-center">
                                    +
                                    {fitnessClass.schedule.sessionDates.length -
                                      6}{" "}
                                    more sessions
                                  </div>
                                )}
                            </div>
                          </div>
                        )}

                      {/* Single Session Details */}
                      {fitnessClass.schedule.kind === "single" && (
                        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200">
                          <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">
                            Session Details
                          </h3>
                          <div className="space-y-2">
                            {fitnessClass.schedule.kind === "single" &&
                              fitnessClass.schedule.date && (
                                <div className="text-xs sm:text-sm">
                                  <span className="text-gray-800 font-medium">
                                    Date:
                                  </span>
                                  <span className="text-gray-700 ml-2">
                                    {new Date(
                                      fitnessClass.schedule.date
                                    ).toLocaleDateString()}
                                  </span>
                                </div>
                              )}
                            {fitnessClass.schedule.kind === "single" &&
                              fitnessClass.schedule.date && (
                                <div className="text-xs sm:text-sm">
                                  <span className="text-gray-800 font-medium">
                                    Time:
                                  </span>
                                  <span className="text-gray-700 ml-2">
                                    {safeFormatDate(fitnessClass.schedule.date, { time: true }) || "N/A"}
                                  </span>
                                </div>
                              )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Equipment Required Section */}
                {fitnessClass.equipmentRequired &&
                  fitnessClass.equipmentRequired.length > 0 && (
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 bg-indigo-500 rounded-lg shadow-md flex items-center justify-center">
                            <svg
                              className="w-4 h-4 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                              />
                            </svg>
                          </div>
                          <div>
                            <h2 className="text-base sm:text-lg font-bold text-gray-900">
                              Equipment Required
                            </h2>
                            <p className="text-gray-600 text-xs sm:text-sm font-medium">
                              What you&apos;ll need to bring or have ready
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                          {fitnessClass.equipmentRequired.map(
                            (item: string, idx: number) => (
                              <div
                                key={idx}
                                className="flex items-start gap-2 sm:gap-3 bg-indigo-50 p-3 sm:p-4 rounded-lg border border-indigo-200"
                              >
                                <div className="flex-shrink-0">
                                  <svg
                                    className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 mt-0.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                </div>
                                <div>
                                  <p className="text-xs sm:text-sm font-medium text-indigo-800">
                                    {item}
                                  </p>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                {/* Benefits Section */}
                {fitnessClass.benefits && fitnessClass.benefits.length > 0 && (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 bg-green-500 rounded-lg shadow-md flex items-center justify-center">
                          <CheckCircleIcon className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h2 className="text-base sm:text-lg font-bold text-gray-900">
                            Class Benefits
                          </h2>
                          <p className="text-gray-600 text-xs sm:text-sm font-medium">
                            What you&apos;ll gain from this experience
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                        {fitnessClass.benefits.map(
                          (benefit: string, idx: number) => (
                            <div
                              key={idx}
                              className="flex items-start gap-2 sm:gap-3 bg-green-50 p-3 sm:p-4 rounded-lg border border-green-200"
                            >
                              <div className="flex-shrink-0">
                                <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mt-0.5" />
                              </div>
                              <div>
                                <p className="text-xs sm:text-sm font-medium text-green-800">
                                  {benefit}
                                </p>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* What to Avoid Section */}
                {fitnessClass.whatToAvoid &&
                  fitnessClass.whatToAvoid.length > 0 && (
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 bg-red-500 rounded-lg shadow-md flex items-center justify-center">
                            <svg
                              className="w-4 h-4 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z"
                              />
                            </svg>
                          </div>
                          <div>
                            <h2 className="text-base sm:text-lg font-bold text-gray-900">
                              What to Avoid
                            </h2>
                            <p className="text-gray-600 text-xs sm:text-sm font-medium">
                              Important things to avoid during this class
                            </p>
                          </div>
                        </div>

                        <div className="space-y-2 sm:space-y-3">
                          {fitnessClass.whatToAvoid.map(
                            (avoidance: string, idx: number) => (
                              <div
                                key={idx}
                                className="flex items-start gap-2 sm:gap-3 bg-red-50 p-3 sm:p-4 rounded-lg border border-red-200"
                              >
                                <div className="flex-shrink-0">
                                  <svg
                                    className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mt-0.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M6 18L18 6M6 6l12 12"
                                    />
                                  </svg>
                                </div>
                                <div>
                                  <p className="text-xs sm:text-sm font-medium text-red-800">
                                    {avoidance}
                                  </p>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Features & Achievements */}
                {((fitnessClass.achievements?.length ?? 0) > 0 ||
                  (fitnessClass.features?.length ?? 0) > 0) && (
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 bg-purple-500 rounded-lg shadow-md flex items-center justify-center">
                            <FaTrophy className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <h2 className="text-base sm:text-lg font-bold text-gray-900">
                              Class Highlights
                            </h2>
                            <p className="text-gray-600 text-xs sm:text-sm font-medium">
                              Special features that make this class unique
                            </p>
                          </div>
                        </div>

                        {/* Achievements */}
                        {fitnessClass.achievements &&
                          fitnessClass.achievements.length > 0 && (
                            <div className="mb-3 sm:mb-4">
                              <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">
                                Achievements
                              </h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3">
                                {fitnessClass.achievements.map(
                                  (achievement: string, idx: number) => (
                                    <div
                                      key={idx}
                                      className="bg-yellow-50 p-2 sm:p-3 rounded-lg border border-yellow-200 text-xs sm:text-sm font-medium text-gray-700"
                                    >
                                      {achievement}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}

                        {/* Features */}
                        {fitnessClass.features &&
                          fitnessClass.features.length > 0 && (
                            <div>
                              <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">
                                Features
                              </h3>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                                {fitnessClass.features.map(
                                  (feature: string, idx: number) => (
                                    <div
                                      key={idx}
                                      className="bg-gray-50 p-2 sm:p-3 rounded-lg border border-gray-200 text-xs sm:text-sm font-medium text-gray-700"
                                    >
                                      {feature}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  )}

                <PrerequisitesSection fitnessClass={fitnessClass} />

                {/* Review Section */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="p-6">
                    {/* Review Header */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className="relative">
                        <div className="w-12 h-12 bg-yellow-500 rounded-lg shadow-lg flex items-center justify-center">
                          <SparklesIcon className="w-6 h-6 text-white" />
                        </div>
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
                      </div>
                      <div>
                        <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                          Reviews & Experiences
                        </h2>
                        <p className="text-gray-600 font-medium text-xs sm:text-sm">
                          What our community says about this transformative
                          class
                        </p>
                      </div>
                      <div className="ml-auto flex items-center gap-3">
                        <button
                          onClick={fetchReviews}
                          disabled={fetchingReviews}
                          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 border border-gray-300 transition-all duration-200 disabled:opacity-50"
                          title="Refresh reviews"
                        >
                          <ArrowPathIcon
                            className={`w-4 h-4 text-gray-600 ${fetchingReviews ? "animate-spin" : ""
                              }`}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Review stats */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <div className="bg-yellow-50 p-3 sm:p-4 rounded-lg border border-yellow-200 text-center">
                        <div className="text-xl sm:text-2xl font-bold text-yellow-600">
                          {fitnessClass.rating || 0}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600 font-medium">
                          Average Rating
                        </div>
                      </div>
                      <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-200 text-center">
                        <div className="text-xl sm:text-2xl font-bold text-blue-600">
                          {reviews.length}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600 font-medium">
                          Total Reviews
                        </div>
                      </div>

                    </div>

                    {/* Add review form */}
                    {user && isClassBooked && !userHasReviewed && (
                      <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 mb-4 sm:mb-6">
                        <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">
                          Share Your Experience
                        </h3>
                        <div className="mb-2 sm:mb-3">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                            Rating
                          </label>
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                onClick={() => setNewRating(star)}
                                className="text-xl sm:text-2xl hover:scale-110 transition-transform"
                              >
                                {star <= newRating ? (
                                  <StarIconSolid className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500" />
                                ) : (
                                  <FaStar className="w-5 h-5 sm:w-6 sm:h-6 text-gray-300" />
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                        <textarea
                          value={newReview}
                          onChange={(e) => setNewReview(e.target.value)}
                          placeholder="Share your thoughts about this class..."
                          className="w-full p-2 sm:p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-xs sm:text-sm"
                          rows={3}
                        />
                        <div className="flex justify-end gap-2 sm:gap-3 mt-2 sm:mt-3">
                          <button
                            onClick={() => {
                              setNewReview("");
                              setNewRating(5);
                            }}
                            className="px-3 sm:px-4 py-1.5 sm:py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleAddReview}
                            disabled={reviewLoading || !newReview.trim()}
                            className="px-3 sm:px-4 py-1.5 sm:py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                          >
                            {reviewLoading ? "Submitting..." : "Submit Review"}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Show message for logged in users who haven't booked */}
                    {user && !isClassBooked && (
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex items-center gap-3 mb-6">
                        <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                        <p className="text-sm text-blue-700 font-medium">
                          Enroll in this class to share your experience with the community!
                        </p>
                      </div>
                    )}

                    {/* Show message for users who have already reviewed */}
                    {user && isClassBooked && userHasReviewed && (
                      <div className="bg-green-50 p-4 rounded-lg border border-green-100 flex items-center gap-3 mb-6">
                        <CheckCircleIcon className="w-5 h-5 text-green-500" />
                        <p className="text-sm text-green-700 font-medium">
                          Thank you for sharing your experience! You've already submitted a review for this class.
                        </p>
                      </div>
                    )}

                    {/* Review filters */}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                      <select
                        value={sortBy}
                        onChange={(e) =>
                          setSortBy(
                            e.target.value as "newest" | "oldest" | "rating"
                          )
                        }
                        className="px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="rating">Highest Rated</option>
                      </select>
                      <select
                        value={filterRating || ""}
                        onChange={(e) =>
                          setFilterRating(
                            e.target.value ? parseInt(e.target.value) : null
                          )
                        }
                        className="px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">All Ratings</option>
                        <option value="5">5 Stars</option>
                        <option value="4">4 Stars</option>
                        <option value="3">3 Stars</option>
                        <option value="2">2 Stars</option>
                        <option value="1">1 Star</option>
                      </select>
                    </div>

                    {/* Reviews list */}
                    <div className="space-y-4">
                      {reviewError && (
                        <div className="text-center py-8 text-red-600 bg-red-50 rounded-lg border border-red-200">
                          {reviewError}
                        </div>
                      )}
                      {fetchingReviews && (
                        <div className="text-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
                          <p className="text-gray-600 mt-2 text-sm">
                            Loading reviews...
                          </p>
                        </div>
                      )}
                      {!fetchingReviews && reviews.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <p>
                            No reviews yet. Be the first to share your
                            experience!
                          </p>
                        </div>
                      )}
                      {reviews.length > 0 &&
                        reviews
                          .filter((review) =>
                            filterRating ? review.rating === filterRating : true
                          )
                          ?.sort((a, b) => {
                            if (sortBy === "newest") {
                              const dateA = new Date(a.createdAt || a.date || 0).getTime();
                              const dateB = new Date(b.createdAt || b.date || 0).getTime();
                              return dateB - dateA;
                            }
                            if (sortBy === "oldest") {
                              const dateA = new Date(a.createdAt || a.date || 0).getTime();
                              const dateB = new Date(b.createdAt || b.date || 0).getTime();
                              return dateA - dateB;
                            }
                            if (sortBy === "rating") {
                              return b.rating - a.rating;
                            }
                            return 0;
                          })
                          ?.map((review, idx) => (
                            <div
                              key={review._id || idx}
                              className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                                    <span className="text-white font-medium text-sm">
                                      {(
                                        review.userName ||
                                        review.reviewerName ||
                                        "Anonymous"
                                      )
                                        .charAt(0)
                                        .toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-gray-900 text-sm">
                                      {review.userName ||
                                        review.reviewerName ||
                                        "Anonymous User"}
                                    </h4>
                                    <div className="flex items-center gap-2">
                                      <div className="flex">
                                        {renderStars(review.rating)}
                                      </div>
                                      <span className="text-xs text-gray-500">
                                        {safeFormatDate(review.createdAt || review.date) || "N/A"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <p className="text-gray-700 text-sm leading-relaxed">
                                {review.comment ||
                                  review.review ||
                                  "No comment provided."}
                              </p>
                            </div>
                          ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Sidebar */}
              <div className="lg:col-span-4 space-y-6">
                <SidebarSection
                  fitnessClass={fitnessClass}
                  handleAction={handleAction}
                  isAuthLoading={isAuthLoading}
                  isBookingLoading={isBookingLoading}
                  isFavorite={isFavorite}
                  handleToggleFavorite={handleToggleFavorite}
                  classId={classId}
                  isClassBooked={isClassBooked}
                  router={router}
                  user={user}
                />
              </div>
            </div>
          </div>

          <Transition
            appear
            show={openModal && actionType === "booking" && !!fitnessClass}
            as={Fragment}
          >
            <Dialog
              as="div"
              className="relative z-50"
              onClose={handleCloseModal}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-[#463a33]/60 backdrop-blur-sm" />
              </Transition.Child>

              {/* Mobile: Bottom Drawer, Desktop: Centered Modal */}
              <div className="fixed inset-0 flex items-end sm:items-center justify-center sm:p-4">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500"
                  enterFrom="translate-y-full sm:translate-y-0 sm:opacity-0 sm:scale-95"
                  enterTo="translate-y-0 sm:opacity-100 sm:scale-100"
                  leave="transform transition ease-in-out duration-300"
                  leaveFrom="translate-y-0 sm:opacity-100 sm:scale-100"
                  leaveTo="translate-y-full sm:translate-y-0 sm:opacity-0 sm:scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-y-auto">
                    {/* Drag Handle - Only visible on mobile */}
                    <div className="sm:hidden flex justify-center pt-3 pb-2 bg-white rounded-t-3xl">
                      <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
                    </div>

                    {fitnessClass && (
                      <PaymentFirstBooking
                        classData={{
                          id: fitnessClass.id,
                          title: fitnessClass.title,
                          instructorName: fitnessClass.instructorName,
                          price: getBaseAmount(fitnessClass),
                          capacity: fitnessClass.capacity,
                          currentEnrollment:
                            fitnessClass.capacity -
                            (fitnessClass.spotsLeft || 0),
                          duration: fitnessClass.schedule.durationMin || 60,
                          // Skip date for now to avoid conversion issues
                          date: undefined,
                        }}
                        userId={user?.id || ""}
                        onBookingSuccess={(booking) => {
                          console.log("Booking successful:", booking);
                          setIsClassBooked(true);
                          setFitnessClass((prev) =>
                            prev
                              ? {
                                ...prev,
                                spotsLeft: Math.max(
                                  0,
                                  (prev.spotsLeft || 0) - 1
                                ),
                              }
                              : null
                          );
                          handleCloseModal();
                          toast.success("Enrolled in class successfully!");
                          router.push("/payments");
                        }}
                        onBookingError={(error) => {
                          console.error("Booking error:", error);
                          toast.error(error);
                          handleCloseModal();
                          router.push("/payments");
                        }}
                        onClose={handleCloseModal}
                      />
                    )}
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </Dialog>
          </Transition>

          {openModal && actionType === "booking-old" && fitnessClass && (
            <div className="fixed inset-0 bg-[#463a33]/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn zoom-container">
              <div className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden animate-scaleIn">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-[#463a33]">
                      Confirm Your Enrollment (Old Flow)
                    </h3>
                    <button
                      onClick={handleCloseModal}
                      className="text-[#463a33]/60 hover:text-[#463a33] transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Class Information Display */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-4 bg-[#463a33]/5 rounded-xl">
                        <FaCalendarAlt className="w-6 h-6 text-[#ff7043]" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-[#463a33]">
                            {fitnessClass?.schedule?.kind === "single"
                              ? safeFormatDate(fitnessClass.schedule.date) || "Single Session"
                              : fitnessClass?.schedule?.kind === "recurring"
                                ? "Recurring Class Package"
                                : fitnessClass?.schedule?.kind === "course"
                                  ? "Course Program"
                                  : "Class Schedule"}
                          </p>
                          <p className="text-sm text-[#463a33]/70">
                            {fitnessClass?.schedule?.kind === "single"
                              ? safeFormatDate(fitnessClass.schedule.date, { time: true }) || "Time TBD"
                              : fitnessClass?.schedule?.kind === "recurring"
                                ? `${fitnessClass.schedule.totalSessions || "Multiple"} sessions - ${fitnessClass.schedule.startTime}`
                                : fitnessClass?.schedule?.kind === "course"
                                  ? `${fitnessClass.schedule.sessionDates?.length || 0} sessions total`
                                  : "Multiple Sessions"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 p-4 bg-[#463a33]/5 rounded-xl">
                        <svg
                          className="w-6 h-6 text-[#ff7043]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-[#463a33]">
                            {fitnessClass?.instructorName || "Instructor"}
                          </p>
                          <p className="text-sm text-[#463a33]/70">
                            {fitnessClass?.instructorType &&
                              INSTRUCTOR_TYPES[
                              fitnessClass.instructorType as keyof typeof INSTRUCTOR_TYPES
                              ]}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-[#463a33]/5 rounded-xl">
                        <div className="flex items-center gap-2">
                          <FaUsers className="w-5 h-5 text-[#463a33]" />
                          <span className="text-sm text-[#463a33]">
                            Spots Available
                          </span>
                        </div>
                        <span className="text-sm font-medium text-[#ff7043]">
                          {fitnessClass?.spotsLeft ||
                            fitnessClass?.capacity ||
                            0}{" "}
                          left
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {/* Price */}
                      <div className="bg-gray-50 border rounded-lg p-4 mb-4">
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-gray-900">Total Amount:</span>
                          <span className="font-bold text-lg text-orange-600">₹{getBaseAmount(fitnessClass).toLocaleString()}</span>
                        </div>
                      </div>

                      {/* Schedule Details */}
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-900 mb-3 text-sm">
                          Schedule Details
                        </h4>
                        {fitnessClass?.schedule?.kind === "recurring" ? (
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-blue-800">Start Date</span>
                              <span className="font-medium text-blue-900">
                                {new Date(
                                  fitnessClass.schedule.startDate
                                ).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-blue-800">Time</span>
                              <span className="font-medium text-blue-900">
                                {fitnessClass.schedule.startTime}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-blue-800">Days</span>
                              <span className="font-medium text-blue-900">
                                {fitnessClass.schedule.weekdayMask
                                  ?.map((dayNum) => {
                                    const dayNames = [
                                      "Sun",
                                      "Mon",
                                      "Tue",
                                      "Wed",
                                      "Thu",
                                      "Fri",
                                      "Sat",
                                    ];
                                    return dayNames[dayNum];
                                  })
                                  .join(", ")}
                              </span>
                            </div>
                            {fitnessClass.schedule.endDate && (
                              <div className="flex justify-between text-sm">
                                <span className="text-blue-800">End Date</span>
                                <span className="font-medium text-blue-900">
                                  {new Date(
                                    fitnessClass.schedule.endDate
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : fitnessClass?.schedule?.kind === "course" ? (
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-blue-800">
                                Total Sessions
                              </span>
                              <span className="font-medium text-blue-900">
                                {fitnessClass.schedule.sessionDates?.length ||
                                  0}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-blue-800">
                                First Session
                              </span>
                              <span className="font-medium text-blue-900">
                                {fitnessClass.schedule.sessionDates?.[0] &&
                                  new Date(
                                    fitnessClass.schedule.sessionDates[0]
                                  ).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-blue-800">Date</span>
                              <span className="font-medium text-blue-900">
                                {fitnessClass?.schedule?.kind === "single" &&
                                  new Date(
                                    fitnessClass.schedule.date
                                  ).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-blue-800">Time</span>
                              <span className="font-medium text-blue-900">
                                {fitnessClass?.schedule?.kind === "single" &&
                                  new Date(
                                    fitnessClass.schedule.date
                                  ).toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Other Details */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#463a33]/70">Duration</span>
                          <span className="font-medium text-[#463a33]">
                            {fitnessClass?.schedule?.durationMin || 0} min
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#463a33]/70">Location</span>
                          <span className="font-medium text-[#463a33]">
                            {fitnessClass?.location || "Online"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleCloseModal}
                        className="flex-1 py-3 border border-[#463a33]/20 text-[#463a33] rounded-xl hover:bg-[#463a33]/5 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleConfirmAction}
                        disabled={isBookingLoading}
                        className="flex-1 flex items-center justify-center gap-2 bg-[#ff7043] text-white py-3 rounded-xl font-medium hover:bg-[#ff7043]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isBookingLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <CurrencyRupeeIcon className="w-5 h-5" />
                            Pay ₹{getBaseAmount(fitnessClass).toLocaleString()}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {openModal && actionType === "join" && (
            <div className="fixed inset-0 bg-[#463a33]/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn zoom-container">
              <div className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden animate-scaleIn">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-[#463a33]">
                      Join Live Session
                    </h3>
                    <button
                      onClick={handleCloseModal}
                      className="text-[#463a33]/60 hover:text-[#463a33] transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#ff7043] to-[#ff7043]/80 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <FaVideo className="text-white w-5 h-5" />
                    </div>
                    <p className="text-[#463a33]/80 mb-6 leading-relaxed text-sm">
                      You&apos;re about to join the live session. Ensure you
                      have a stable connection for the best experience.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleCloseModal}
                        className="flex-1 py-3 border border-[#463a33]/20 text-[#463a33] rounded-xl hover:bg-[#463a33]/5 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleConfirmAction}
                        className="flex-1 flex items-center justify-center gap-2 bg-[#ff7043] text-white py-3 rounded-xl font-medium hover:bg-[#ff7043]/90 transition-colors"
                      >
                        <FaVideo className="w-5 h-5" />
                        Join Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {openModal && actionType === "livestream" && (
            <div className="fixed inset-0 bg-[#463a33]/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
              <div className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden animate-scaleIn">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-[#463a33]">
                      Join Live Stream
                    </h3>
                    <button
                      onClick={handleCloseModal}
                      className="text-[#463a33]/60 hover:text-[#463a33] transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <div className="relative">
                        <FaVideo className="text-white w-5 h-5" />
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-300 rounded-full animate-ping"></div>
                      </div>
                    </div>
                    <p className="text-[#463a33]/80 mb-6 leading-relaxed text-sm">
                      🔴 This class is currently live streaming! Join now to
                      participate in real-time.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleCloseModal}
                        className="flex-1 py-3 border border-[#463a33]/20 text-[#463a33] rounded-xl hover:bg-[#463a33]/5 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleConfirmAction}
                        className="flex-1 flex items-center justify-center gap-2 bg-red-500 text-white py-3 rounded-xl font-medium hover:bg-red-600 transition-colors"
                      >
                        <FaVideo className="w-5 h-5" />
                        Join Stream
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <style jsx global>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }

            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            @keyframes slideIn {
              from {
                opacity: 0;
                transform: translateX(20px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }

            @keyframes scaleIn {
              from {
                opacity: 0;
                transform: scale(0.95);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }

            @keyframes slideUpFromBottom {
              from {
                opacity: 0;
                transform: translateY(100%);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }

            .animate-fadeIn {
              animation: fadeIn 0.3s ease-out;
            }

            .animate-slideUp {
              animation: slideUp 0.5s ease-out;
            }

            .animate-slideIn {
              animation: slideIn 0.5s ease-out;
            }

            .animate-scaleIn {
              animation: scaleIn 0.3s ease-out;
            }

            .animate-slideUpFromBottom {
              animation: slideUpFromBottom 0.4s cubic-bezier(0.16, 1, 0.3, 1);
            }
          `}</style>
        </>
      )}

      <Footer />
    </div>
  );
};

export default ClassDetailClient;
